import React from 'react';
import { Database, BadgeCheck, Download, Search, Upload, FileSpreadsheet, Filter, Workflow } from 'lucide-react';

const GhlFeatures: React.FC = () => {
  return (
    <section id="features" className="py-24 bg-slate-900 border-y border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* HEADER */}
        <div className="text-center mb-20">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
            Built for GoHighLevel Power Users
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Everything you need to fuel your GHL campaigns with verified professional license data.
            Search, filter, export, import. That simple.
          </p>
        </div>

        {/* FEATURES GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-32">
          <div className="space-y-4">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-emerald-400" />
            </div>
            <h3 className="text-lg font-bold text-white">18M+ Records</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Dentists, plumbers, electricians, real estate agents, contractors, attorneys, insurance agents,
              and 50+ more licensed professions across all 50 states.
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-10 h-10 bg-sky-500/10 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-sky-400" />
            </div>
            <h3 className="text-lg font-bold text-white">GHL-Ready CSV Format</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Exports include First Name, Last Name, Email, Phone, Address, City, State, ZIP.
              Maps directly to GHL contact import fields with zero cleanup.
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
              <BadgeCheck className="w-5 h-5 text-amber-400" />
            </div>
            <h3 className="text-lg font-bold text-white">Verified Licenses</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Every record is verified against official state licensing boards. Know license status,
              expiration date, and whether the professional is actively practicing.
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-10 h-10 bg-indigo-500/10 rounded-lg flex items-center justify-center">
              <Filter className="w-5 h-5 text-indigo-400" />
            </div>
            <h3 className="text-lg font-bold text-white">Granular Filters</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Filter by state, city, ZIP code, profession, license status, and more.
              Build hyper-targeted lists for each GHL sub-account or campaign.
            </p>
          </div>
        </div>

        {/* HOW IT WORKS - GHL WORKFLOW */}
        <div className="relative rounded-3xl bg-slate-950 p-8 md:p-12 border border-slate-800">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900 border border-slate-800 px-6 py-2 rounded-full text-sm font-bold text-white shadow-xl">
            GHL WORKFLOW
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-6">
            {/* Step 1 */}
            <div className="text-center relative">
              <div className="w-16 h-16 mx-auto bg-slate-900 rounded-full flex items-center justify-center border border-slate-800 mb-6 shadow-lg relative z-10">
                <Search className="w-6 h-6 text-emerald-500" />
              </div>
              <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-slate-800 -z-0"></div>
              <h4 className="text-white font-bold mb-2">1. Search</h4>
              <p className="text-xs text-slate-500">"Dentists in Miami, FL" or "Plumbers in 75001"</p>
            </div>

            {/* Step 2 */}
            <div className="text-center relative">
              <div className="w-16 h-16 mx-auto bg-slate-900 rounded-full flex items-center justify-center border border-slate-800 mb-6 shadow-lg relative z-10">
                <Download className="w-6 h-6 text-emerald-500" />
              </div>
              <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-slate-800 -z-0"></div>
              <h4 className="text-white font-bold mb-2">2. Export CSV</h4>
              <p className="text-xs text-slate-500">Download a clean, GHL-formatted CSV file</p>
            </div>

            {/* Step 3 */}
            <div className="text-center relative">
              <div className="w-16 h-16 mx-auto bg-slate-900 rounded-full flex items-center justify-center border border-slate-800 mb-6 shadow-lg relative z-10">
                <Upload className="w-6 h-6 text-emerald-500" />
              </div>
              <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-slate-800 -z-0"></div>
              <h4 className="text-white font-bold mb-2">3. Import to GHL</h4>
              <p className="text-xs text-slate-500">Upload contacts into your GHL sub-account</p>
            </div>

            {/* Step 4 */}
            <div className="text-center relative">
              <div className="w-16 h-16 mx-auto bg-slate-900 rounded-full flex items-center justify-center border border-slate-800 mb-6 shadow-lg relative z-10">
                <Workflow className="w-6 h-6 text-emerald-500" />
              </div>
              <h4 className="text-white font-bold mb-2">4. Launch Campaign</h4>
              <p className="text-xs text-slate-500">Trigger your GHL workflow and start converting</p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default GhlFeatures;
